import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Search, ShieldCheck, Users } from 'lucide-react';
import { api, getApiError } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { EmptyState, LoadingState, PageHeader } from '@/components/ui';
import { localeFor } from '@/components/reports/chartTheme';
import { AdminPagination } from './AdminPagination';
import { ConfirmDialog } from './ConfirmDialog';
import { useDebouncedValue } from './useDebouncedValue';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  mfaEnabled: boolean;
  createdAt: string;
}

interface UsersResponse {
  users: User[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

const ROLES = ['DOCTOR', 'PHARMACIST', 'PATIENT', 'ADMIN', 'AUDITOR'] as const;

const roleBadges: Record<string, string> = {
  DOCTOR: 'badge-primary',
  PHARMACIST: 'badge-success',
  PATIENT: 'badge-gray',
  ADMIN: 'badge-danger',
  AUDITOR: 'badge-warning',
};

export function UserManagement() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [pendingToggle, setPendingToggle] = useState<User | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['users', page, roleFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (roleFilter) params.set('role', roleFilter);
      if (search) params.set('search', search);
      const response = await api.get(`/users?${params}`);
      return response.data as UsersResponse;
    },
    placeholderData: keepPreviousData,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      await api.patch(`/users/${id}`, { isActive });
    },
    onSuccess: () => {
      setPendingToggle(null);
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => setActionError(getApiError(err).message),
  });

  const requestToggle = (user: User) => {
    setActionError(null);
    toggleActiveMutation.reset();
    // Re-activating is harmless; only deactivation asks for confirmation.
    if (user.isActive) {
      setPendingToggle(user);
    } else {
      toggleActiveMutation.mutate({ id: user.id, isActive: true });
    }
  };

  const users = data?.users ?? [];
  const pagination = data?.pagination;
  const hasFilters = Boolean(search || roleFilter);
  const locale = localeFor(i18n.language);

  return (
    <div>
      <PageHeader title={t('nav.users')} description={t('admin.users.description')} />

      {/* Filters */}
      <div className="card mb-6 p-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label htmlFor="user-search" className="label">
              {t('common.search')}
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                id="user-search"
                type="search"
                className="input ps-9"
                placeholder={t('admin.users.searchPlaceholder')}
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
          <div>
            <label htmlFor="user-role" className="label">
              {t('auth.role')}
            </label>
            <select
              id="user-role"
              className="input"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('admin.users.allRoles')}</option>
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {t(`roles.${role.toLowerCase()}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {actionError && !pendingToggle && (
        <div role="alert" className="alert-danger mb-4">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>{actionError}</span>
        </div>
      )}

      {/* Users table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <EmptyState
            icon={AlertCircle}
            title={t('admin.loadError')}
            description={getApiError(error).message}
            action={
              <button type="button" className="btn-secondary" onClick={() => refetch()}>
                {t('admin.retry')}
              </button>
            }
          />
        ) : users.length === 0 ? (
          <EmptyState
            icon={Users}
            title={hasFilters ? t('common.noResults') : t('admin.users.empty')}
            description={hasFilters ? t('admin.users.emptyFiltered') : undefined}
          />
        ) : (
          <>
            <div className={`overflow-x-auto transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('admin.users.user')}</th>
                    <th scope="col">{t('auth.role')}</th>
                    <th scope="col">{t('admin.users.mfa')}</th>
                    <th scope="col">{t('admin.users.status')}</th>
                    <th scope="col">{t('admin.users.createdAt')}</th>
                    <th scope="col" className="text-end">
                      <span className="sr-only">{t('admin.users.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isSelf = user.id === currentUserId;
                    const isRowPending =
                      toggleActiveMutation.isPending && toggleActiveMutation.variables?.id === user.id;
                    return (
                      <tr key={user.id}>
                        <td className="max-w-xs">
                          <p className="truncate font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                            {isSelf && (
                              <span className="ms-2 text-xs font-normal text-gray-400">
                                ({t('admin.users.you')})
                              </span>
                            )}
                          </p>
                          <p className="truncate text-xs text-gray-500" title={user.email}>
                            {user.email}
                          </p>
                        </td>
                        <td>
                          <span className={roleBadges[user.role] || 'badge-gray'}>
                            {t(`roles.${user.role.toLowerCase()}`)}
                          </span>
                        </td>
                        <td>
                          {user.mfaEnabled ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-success-700">
                              <ShieldCheck className="h-4 w-4" />
                              {t('admin.users.mfaOn')}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">{t('admin.users.mfaOff')}</span>
                          )}
                        </td>
                        <td>
                          {user.isActive ? (
                            <span className="badge-success">{t('admin.users.active')}</span>
                          ) : (
                            <span className="badge-gray">{t('admin.users.inactive')}</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap text-xs text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString(locale)}
                        </td>
                        <td className="whitespace-nowrap text-end">
                          <button
                            type="button"
                            onClick={() => requestToggle(user)}
                            disabled={isSelf || isRowPending}
                            title={isSelf ? t('admin.users.cannotDeactivateSelf') : undefined}
                            className={`btn-sm ${
                              user.isActive
                                ? 'btn text-danger-700 hover:bg-danger-50 focus-visible:ring-danger-500'
                                : 'btn text-primary-700 hover:bg-primary-50 focus-visible:ring-primary-500'
                            }`}
                          >
                            {isRowPending && (
                              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            )}
                            {user.isActive ? t('admin.users.deactivate') : t('admin.users.activate')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pagination && (
              <AdminPagination
                page={page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                onPageChange={setPage}
                disabled={isFetching}
              />
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={pendingToggle !== null}
        title={t('admin.users.confirmDeactivateTitle')}
        description={
          pendingToggle &&
          t('admin.users.confirmDeactivateBody', {
            name: `${pendingToggle.firstName} ${pendingToggle.lastName}`,
            email: pendingToggle.email,
          })
        }
        confirmLabel={t('admin.users.deactivate')}
        isPending={toggleActiveMutation.isPending}
        error={actionError}
        onConfirm={() => pendingToggle && toggleActiveMutation.mutate({ id: pendingToggle.id, isActive: false })}
        onCancel={() => {
          setPendingToggle(null);
          setActionError(null);
        }}
      />
    </div>
  );
}
